import {
  RequirementType,
  SubTaskTemplate,
  SubTaskType,
  TaskTemplateCompletionCritertia
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { TextField } from '@material-ui/core';
import { useUnsafeTranslation } from '../../../../../../i18next';
import * as UX from '../../newux/kit';

const CompletionCriteriaSelect = styled('select')`
  font-family: inherit;
  font-size: ${UX.tokens.font.variant.caption.fontSize}px;
  font-weight: ${UX.tokens.font.weightScale.regular};
  color: ${UX.tokens.color.black};
  border: 0;
  border-radius: ${UX.tokens.radius.sm}px;
  background: ${UX.tokens.color.white};
  padding: ${UX.tokens.space(1)} ${UX.tokens.space(2)};
  box-shadow: ${UX.ring()};
  cursor: pointer;
  &:focus {
    outline: none;
    box-shadow: ${UX.ringActive};
  }
`;

const CompletionCriteriaWrite = styled('div')`
  display: grid;
  grid-template-columns: max-content 4fr max-content;
  grid-gap: ${(p) => p.theme.spacing(1)}px;
  align-items: center;
  padding-bottom: ${(p) => p.theme.spacing(1)}px;
`;

export const getMininumRequiredCount = (
  value: TaskTemplateCompletionCritertia[]
) => {
  if (value.length > 2) {
    console.log('Cannot support multiple completion criteria just yet!');
  }
  const criterium = value[0];
  if (!criterium) {
    return null;
  }

  if (criterium && criterium.type !== 'SUBSET') {
    console.log('Criterium type not supported yet!');
    return null;
  }

  return criterium.minimumRequired;
};

export const CompletionCriteriaReadOnly = ({
  value,
  subTasks
}: {
  value: TaskTemplateCompletionCritertia[];
  subTasks: SubTaskTemplate[];
}) => {
  const { t } = useUnsafeTranslation();
  if (value.length > 2) {
    console.log('Cannot support multiple completion criteria just yet!');
  }
  const criterium = value[0];
  if (!criterium) {
    return <div>{t('taskCompletionCriteria.builder.completeAll')}</div>;
  }

  if (criterium && criterium.type !== 'SUBSET') {
    console.log('Criterium type not supported yet!');
    return null;
  }

  const requiredSubtasks = subTasks.filter(
    (f) => f.requirementType === RequirementType.MANDATORY
  );

  return (
    <div>
      {criterium.minimumRequired}
      {t('of {{count}} required sub-tasks', {
        count: requiredSubtasks.length
      })}
    </div>
  );
};

export const CompletionCriteriaSelector = ({
  value,
  onChange,
  subTasks,
  newUx = false
}: {
  value: TaskTemplateCompletionCritertia[];
  onChange: (nextValue: TaskTemplateCompletionCritertia[]) => void;
  subTasks: SubTaskTemplate[];
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  // this UI is drastically simplified compared to the power of the data model.
  // currently there can either be no, or only one criterium, which is of the type SUBSET

  if (value.length > 2) {
    console.log('Cannot support multiple completion criteria just yet!');
  }
  const criterium = value[0];

  if (criterium && criterium.type !== 'SUBSET') {
    console.log('Criterium type not supported yet!');
    return null;
  }

  const requiredSubtasks = subTasks.filter(
    (f) => f.requirementType === RequirementType.MANDATORY
  );

  // NEW_UX: the prototype's completion-criteria block (editor-panels.jsx
  // 573-602). Same SUBSET/minimumRequired model as classic — only the
  // presentation differs: a "Complete any [N] of these M sub-tasks" dropdown,
  // a bulleted list of the required sub-tasks, a plain-language summary, and a
  // dimmed list of the optional sub-tasks (which never count toward completion).
  if (newUx) {
    const optionalSubtasks = subTasks.filter(
      (f) => f.requirementType !== RequirementType.MANDATORY
    );
    const requiredCount = requiredSubtasks.length;
    const minimumRequired = Math.min(
      Math.max(1, criterium?.minimumRequired || requiredCount),
      requiredCount || 1
    );
    // Mirror the prototype's `sub.label || 'Subtask'`. Our sub-tasks carry the
    // author-set label/title on `data`; fall back to a generic term.
    // QUESTION sub-tasks keep their prompt under `data.question.label`, so surface
    // it as `Answer "<question>"` rather than the generic "Sub-task".
    const subLabel = (s: SubTaskTemplate) => {
      if (s.type === SubTaskType.QUESTION) {
        const q = (s.data as { question?: { label?: string } })?.question;
        const qLabel = (q?.label || '').trim();
        return qLabel
          ? t('Answer "{{ question }}"', { question: qLabel })
          : t('Answer a question');
      }
      const d = s.data as Partial<{ label: string; title: string }>;
      return (d?.label || d?.title || '').trim() || t('Sub-task');
    };
    const { fontSize: captionSize } = UX.tokens.font.variant.caption;
    const { regular: weightRegular } = UX.tokens.font.weightScale;
    const { space } = UX.tokens;
    const listStyle: React.CSSProperties = {
      margin: 0,
      paddingLeft: 28
    };
    const listItemStyle: React.CSSProperties = {
      padding: '2px 0',
      fontSize: captionSize
    };
    return (
      <div style={{ marginTop: space(3) }}>
        {requiredCount === 0 ? (
          <p
            style={{
              fontSize: captionSize,
              fontWeight: weightRegular,
              color: UX.tokens.color.black,
              margin: 0
            }}
          >
            {optionalSubtasks.length > 0
              ? t('Only optional subtasks. This task will complete when any 1 subtask is done.')
              : t('N/A – nothing will be sent to this segment')}
          </p>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: space(2),
                marginBottom: space(1),
                fontSize: captionSize,
                fontWeight: weightRegular,
                color: UX.tokens.color.black
              }}
            >
              {t('Complete any')}
              <CompletionCriteriaSelect
                value={minimumRequired}
                onChange={(ev) =>
                  onChange([
                    { type: 'SUBSET', minimumRequired: +ev.target.value }
                  ])
                }
              >
                {Array.from({ length: requiredCount }, (_, i) => i + 1).map(
                  (n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  )
                )}
              </CompletionCriteriaSelect>
              {t('of these {{ total }} subtasks', { total: requiredCount })}
            </div>
            <ul style={listStyle}>
              {requiredSubtasks.map((sub) => (
                <li
                  key={sub.id}
                  style={{
                    ...listItemStyle,
                    fontWeight: weightRegular,
                    color: UX.tokens.color.black
                  }}
                >
                  {subLabel(sub)}
                </li>
              ))}
            </ul>
          </>
        )}
        {optionalSubtasks.length > 0 && (
          <>
            {requiredCount > 0 && (
              <span
                style={{
                  fontSize: captionSize,
                  fontWeight: weightRegular,
                  color: UX.tokens.color.black,
                  display: 'block',
                  fontStyle: 'italic',
                  margin: `${space(3)} 0 ${space(1)}`
                }}
              >
                {t('Optional subtasks (not considered when determining task completion)')}:
              </span>
            )}
            <ul style={listStyle}>
              {optionalSubtasks.map((sub) => (
                <li
                  key={sub.id}
                  style={{
                    ...listItemStyle,
                    fontWeight: weightRegular,
                    color: UX.tokens.color.black
                  }}
                >
                  {subLabel(sub)}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <CompletionCriteriaWrite>
      <TextField
        type="number"
        value={criterium?.minimumRequired || requiredSubtasks.length}
        variant="outlined"
        disabled={requiredSubtasks.length === 0}
        inputProps={{ min: 1, max: requiredSubtasks.length }}
        onChange={(ev) => {
          const nextCount = +ev.target.value;
          onChange([
            {
              type: 'SUBSET',
              minimumRequired: nextCount
            }
          ]);
        }}
      ></TextField>
      <div>
        {t('of {{count}} required sub-tasks', {
          count: requiredSubtasks.length
        })}
      </div>
    </CompletionCriteriaWrite>
  );
};
